import React from 'react';
import { computed, toJS } from 'mobx';
import FormInput from './form_input';
import FormList from './form_list';
import FormSelect from './form_select';
import FormFlatSelect from './form_flat_select';
import FormCheckbox from './form_checkbox';
import FormTextarea from './form_textarea';
import FormToggle from './form_toggle';
import FormFlagSelect from './form_flag_select';
import { isPresent, isBlank, isValidEmail } from '@seedlang/utils';

class FormFactory {

  constructor(store, props) {
    if (isPresent(store)) {
      this.store = store;
    } else {
      console.warn('Your form is being instantiated with an empty store.');
    }
    this.props = props || {};
  }

  @computed get isUpdate() {
    return this.store.hasShowData;
  }

  @computed get submissionEnabled() {
    return this.validated && this.store.dirtyForm;
  }

  @computed get validated() {
    const requiredFieldsValid = isBlank(toJS(this.store.requiredFields)
      .find(field => isBlank(toJS(this.store.formCache)[field])));

    const emailFieldsValid = Object.entries(toJS(this.store.formCache))
      .filter(([key]) => key.includes('email'))
      .every(([, value]) => isValidEmail(value));

    return requiredFieldsValid && emailFieldsValid;
  }

  onChange = (field, value) => {
    this.store.setFormCacheValue(field, value);
  }

  onSubmit() {
    if (this.isUpdate) {
      this.store.update({
        ids: toJS(this.props.ids),
        data: this.store.serializedFormCache,
        failureCallback: this.props.onAfterUpdate,
      }, this.props.onAfterUpdate);
    } else {
      this.store.create({
        ids: toJS(this.props.ids),
        data: this.store.serializedFormCache,
        failureCallback: this.props.onAfterCreate,
      }, this.props.onAfterCreate);
    }
    if (this.props.resetFormOnSubmit) {
      this.store.resetFormCache();
    } else {
      this.store.resetPristine();
    }
  }

  onClear() {
    this.store.resetFormCache();
  }

  getValue(fieldName) {
    return this.store.formCache[fieldName];
  }

  registerComponent(props) {
    const value = this.store.hasShowData ? this.store.showData[props.name] : props.defaultValue;
    this.store.initializeFormCacheValue(props.name, value);
    this.store.registerFormField(props.name, props.required);
  }

  unregisterComponent() {}

  input(props) {
    return (
      <FormInput
        store={this.store}
        onChange={this.onChange}
        form={this}
        {...props}
      />
    );
  }

  select(props) {
    return (
      <FormSelect
        store={this.store}
        onChange={this.onChange}
        form={this}
        {...props}
      />
    );
  }

  flatSelect(props) {
    return (
      <FormFlatSelect
        store={this.store}
        onChange={this.onChange}
        form={this}
        {...props}
      />
    );
  }

  checkbox(props) {
    return (
      <FormCheckbox
        store={this.store}
        onChange={this.onChange}
        form={this}
        {...props}
      />
    );
  }

  toggle(props) {
    return (
      <FormToggle
        store={this.store}
        onChange={this.onChange}
        form={this}
        {...props}
      />
    );
  }

  list(props) {
    return (
      <FormList
        store={this.store}
        onChange={this.onChange}
        form={this}
        {...props}
      />
    );
  }

  textarea(props) {
    return (
      <FormTextarea
        store={this.store}
        onChange={this.onChange}
        form={this}
        {...props}
      />
    );
  }

  flagSelect(props) {
    return (
      <FormFlagSelect
        store={this.store}
        onChange={this.onChange}
        form={this}
        {...props}
      />
    );
  }
}

export default FormFactory;
